import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import Loading from "../../components/Loading";

import Item from './components/Item';

import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function List() {
	const [loading, setLoading] = useState(true);
	let [users, setUsers] = useState([]);
	const [search, setSearch] = useState("");
	const [activationLoadingId, setActivationLoadingId] = useState(null);
	const [importLoading, setImportLoading] = useState(false);
	const [importUploadProgress, setImportUploadProgress] = useState(0);
	const [importResult, setImportResult] = useState(null);
	const [importError, setImportError] = useState("");

	useEffect(async () => {
		await refreshUsers();
	}, [])

	const refreshUsers = async () => {
		setLoading(true);
		const users = await Data.fetchUsers();
		setUsers(users.data);
		setLoading(false);
	}

	const exportCsv = async () => {
		const response = await Data.exportUsers();
		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'users.csv');
		document.body.appendChild(link);
		link.click();
		link.parentNode.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	const exportImportTemplate = async () => {
		const response = await Data.exportUserImportTemplate();
		const blob = new Blob([response.data], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'users-import-template.csv');
		document.body.appendChild(link);
		link.click();
		link.parentNode.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	const importCsv = async (event) => {
		const file = event.target.files?.[0];
		if(!file) return;

		setImportLoading(true);
		setImportUploadProgress(0);
		setImportResult(null);
		setImportError("");

		try {
			let finalResult = null;
			const response = await Data.importUsers(file, (progressEvent) => {
				if(!progressEvent.total) return;
				setImportUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
			}, (progress) => {
				finalResult = progress;
				setImportResult(progress);
			});
			if(!finalResult && response.data) {
				const lines = String(response.data).trim().split('\n').filter(Boolean);
				if(lines.length) setImportResult(JSON.parse(lines[lines.length - 1]));
			}
			await refreshUsers();
		} catch (error) {
			setImportError(error.response?.data?.message || "User import failed.");
		} finally {
			setImportLoading(false);
			event.target.value = "";
		}
	}

	const downloadFailedRecords = () => {
		if(!importResult?.failedRecords?.length) return;
		const headers = ['row', 'firstName', 'lastName', 'email', 'phoneNumber', 'walletBalance', 'marketingSubscribed', 'isActive', 'message'];
		const escape = (value) => {
			if(value === null || value === undefined) return '';
			const text = String(value);
			return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
		};
		const lines = importResult.failedRecords.map((failed) => {
			const record = failed.record || {};
			return headers.map((header) => escape(header === 'row' || header === 'message' ? failed[header] : record[header])).join(',');
		});
		const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'failed-user-import-records.csv');
		document.body.appendChild(link);
		link.click();
		link.parentNode.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	const toggleActivation = async (userId, active) => {
		setActivationLoadingId(userId);
		try {
			await Data.setUserActivation(userId, active);
			await refreshUsers();
		} catch (error) {
			console.error('Failed to update activation', error);
		} finally {
			setActivationLoadingId(null);
		}
	}

	if(loading) return <Loading button={false} />

	users = users.filter((user) => {
		const query = `${user.firstName} ${user.lastName} (${user.email})`;
		return query.toLowerCase().includes(search.toLowerCase());
	})

	return (
		<div className={styles.list}>
			<input type="text" value={search} onChange={(e) => { setSearch(e.target.value) }} placeholder="Search by name or email..." />
			<button onClick={exportCsv} className={styles.button}>Export Users</button>
			<button onClick={exportImportTemplate} className={styles.button}>Download Import Template</button>
			<label className={styles.button}>
				{importLoading ? 'Importing Users...' : 'Import Users'}
				<input type="file" accept=".csv,text/csv" onChange={importCsv} disabled={importLoading} style={{ display: 'none' }} />
			</label>

			{importLoading && (
				<div>
					<p>Upload progress: {importUploadProgress}%</p>
					<progress value={importUploadProgress} max="100" style={{ width: '100%' }} />
					{importResult ? (
						<p>Stored: {importResult.stored} / {importResult.total} | Left: {importResult.remaining}</p>
					) : (
						<p>Processing starts after the CSV upload completes.</p>
					)}
				</div>
			)}

			{importError && <p>{importError}</p>}

			{importResult && (
				<div>
					<p>Stored: {importResult.stored} / {importResult.total}</p>
					<p>Created: {importResult.created} | Updated: {importResult.updated} | Failed: {importResult.skipped} | Left: {importResult.remaining}</p>
					{!!importResult.failedRecords?.length && (
						<button onClick={downloadFailedRecords} className={styles.button}>Download Failed Records</button>
					)}
				</div>
			)}

			{!users.length && <div><p>There aren't any users to show.</p></div>}

			{!!users.length && (
				<div className={styles.tableWrap}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>User</th>
								<th>Status</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{users.map((user) => (
								<Item
									key={user._id}
									{...user}
									onToggle={toggleActivation}
									loading={activationLoadingId === user._id}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

export default List;
